import i18next from 'i18next'
import { useEffect, useRef, useState } from 'react'

import { isSpeechRecognitionSupported } from '@/hooks/use-voice-goals'
import { transcriptFromResults } from '@/lib/speech-transcript'

type Recognition = {
  lang: string
  continuous: boolean
  interimResults: boolean
  onresult:
    | ((e: {
        resultIndex: number
        results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>
      }) => void)
    | null
  onerror: (() => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
}

// Web Speech dictation into a text field. Appends spoken words to the current
// value (interim results update live, finalized text is kept). Shared by the
// coach input, the new-goal input, and the inbox quick-add.
export function useDictation(opts: {
  getValue: () => string
  onChange: (value: string) => void
  onUpdate?: () => void
}) {
  const [listening, setListening] = useState(false)
  const recognitionRef = useRef<Recognition | null>(null)
  const baseRef = useRef('')

  function stop() {
    recognitionRef.current?.stop()
    recognitionRef.current = null
    setListening(false)
  }

  function toggle() {
    if (listening) {
      stop()
      return
    }
    const Ctor =
      (window as unknown as { SpeechRecognition?: new () => Recognition }).SpeechRecognition ??
      (window as unknown as { webkitSpeechRecognition?: new () => Recognition })
        .webkitSpeechRecognition
    if (!Ctor) return
    // Guard against a lingering instance so two recognizers can't both stream
    // into the field (a classic cause of duplicated words).
    recognitionRef.current?.stop()
    const rec = new Ctor()
    rec.lang = i18next.language || 'en'
    rec.continuous = true
    rec.interimResults = true
    // The field's value before dictation started; spoken words are appended to it.
    baseRef.current = opts.getValue().trim()
    rec.onresult = (e) => {
      const spoken = transcriptFromResults(e.results)
      const base = baseRef.current
      opts.onChange(base && spoken ? `${base} ${spoken}` : base || spoken)
      opts.onUpdate?.()
    }
    rec.onerror = () => {
      recognitionRef.current = null
      setListening(false)
    }
    rec.onend = () => {
      recognitionRef.current = null
      setListening(false)
    }
    recognitionRef.current = rec
    rec.start()
    setListening(true)
  }

  useEffect(() => () => recognitionRef.current?.stop(), [])

  return { listening, supported: isSpeechRecognitionSupported(), toggle, stop }
}
